import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Clock, MapPin, Settings, Save, Loader2, CheckCircle, AlertTriangle, Link2, ExternalLink } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { useNavigate } from 'react-router-dom';
import MapPinPicker from '../../components/MapPinPicker';

const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AttendanceSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [centralWorkingDays, setCentralWorkingDays] = useState({ working_days: [], is_configured: false });
  const [settings, setSettings] = useState({
    office_start_time: '09:00',
    office_end_time: '17:00',
    late_after_minutes: 15,
    half_day_after_minutes: 120,
    grace_period_minutes: 5,
    attendance_radius: 500,
    require_location: true,
    auto_checkout_time: '20:00',
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    location: {
      latitude: '',
      longitude: '',
      address: ''
    },
    // Penalty & Conversion Rules
    late_to_absent_count: 3,  // X late = 1 absent
    half_day_to_absent_count: 2,  // X half day = 1 absent
    monthly_late_limit: 5,  // Max allowed lates per month
    deduction_per_late: 0,  // Salary deduction per late
    deduction_per_absent: 0,  // Salary deduction per absent
    notify_on_late: false,  // Send notification on late
    notify_on_absent: false,  // Send notification on absent
    overtime_rate_per_hour: 0,  // OT calculation
    min_overtime_hours: 1  // Minimum hours for OT
  });

  useEffect(() => {
    fetchSettings();
    fetchCentralWorkingDays();
  }, []);

  const fetchCentralWorkingDays = async () => {
    try {
      const response = await apiClient.get('/api/master-settings/working-days/list');
      setCentralWorkingDays(response.data);
    } catch (error) {
      console.error('Error fetching central working days:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/api/master-settings/attendance-settings');
      if (response.data) {
        setSettings({
          ...settings,
          ...response.data,
          location: response.data.location || { latitude: '', longitude: '', address: '' }
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.post('/api/master-settings/attendance-settings', settings);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setSettings(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day]
    }));
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const accuracy = Math.round(position.coords.accuracy || 0);
          setSettings(prev => ({
            ...prev,
            location: {
              ...prev.location,
              latitude: position.coords.latitude.toFixed(6),
              longitude: position.coords.longitude.toFixed(6)
            }
          }));
          if (accuracy > 100) {
            toast.warning(`Location captured but GPS accuracy is poor (±${accuracy}m). For best results, capture from a mobile phone standing AT the campus, or enter exact coordinates from Google Maps.`);
          } else {
            toast.success(`Location captured! (accuracy ±${accuracy}m)`);
          }
        },
        (error) => {
          toast.error('Failed to get location: ' + error.message);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      toast.error('Geolocation not supported');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Configure office hours and attendance rules for staff</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
          {saving ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Save className="h-4 w-4 mr-2" /> Save Settings</>
          )}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Office Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              Office Hours
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time">Office Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={settings.office_start_time}
                  onChange={(e) => setSettings({...settings, office_start_time: e.target.value})}
                />
                <p className="text-xs text-gray-500">Staff arriving after this is late</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">Office End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={settings.office_end_time}
                  onChange={(e) => setSettings({...settings, office_end_time: e.target.value})}
                />
                <p className="text-xs text-gray-500">Regular office closing time</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="auto_checkout">Auto Checkout Time</Label>
              <Input
                id="auto_checkout"
                type="time"
                value={settings.auto_checkout_time}
                onChange={(e) => setSettings({...settings, auto_checkout_time: e.target.value})}
              />
              <p className="text-xs text-gray-500">Staff will be auto checked-out if not done manually</p>
            </div>
          </CardContent>
        </Card>

        {/* Late & Half Day Rules */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-purple-500" />
              Attendance Rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="grace">Grace Period (minutes)</Label>
              <Input
                id="grace"
                type="number"
                min="0"
                max="30"
                value={settings.grace_period_minutes}
                onChange={(e) => setSettings({...settings, grace_period_minutes: parseInt(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500">No penalty if arriving within this time after office start</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="late">Late After (minutes)</Label>
              <Input
                id="late"
                type="number"
                min="0"
                value={settings.late_after_minutes}
                onChange={(e) => setSettings({...settings, late_after_minutes: parseInt(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500">Marked as "Late" if arriving after grace + this time</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="halfday">Half Day After (minutes)</Label>
              <Input
                id="halfday"
                type="number"
                min="0"
                value={settings.half_day_after_minutes}
                onChange={(e) => setSettings({...settings, half_day_after_minutes: parseInt(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500">Marked as "Half Day" if arriving after this time</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="monthly_limit">Monthly Late Limit</Label>
              <Input
                id="monthly_limit"
                type="number"
                min="0"
                value={settings.monthly_late_limit}
                onChange={(e) => setSettings({...settings, monthly_late_limit: parseInt(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500">Maximum lates allowed per month (0 = unlimited)</p>
            </div>
          </CardContent>
        </Card>

        {/* Penalty & Conversion Rules */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Penalty & Conversion Rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 p-3 rounded-lg text-sm text-red-700 mb-2">
              <strong>Important:</strong> These rules affect salary calculation and attendance reports
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="late_to_absent">Late → Absent Conversion</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="late_to_absent"
                    type="number"
                    min="1"
                    max="10"
                    className="w-20"
                    value={settings.late_to_absent_count}
                    onChange={(e) => setSettings({...settings, late_to_absent_count: parseInt(e.target.value) || 3})}
                  />
                  <span className="text-sm text-gray-600">Late = 1 Absent</span>
                </div>
                <p className="text-xs text-gray-500">Example: 3 late = 1 absent count</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="halfday_to_absent">Half Day → Absent Conversion</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="halfday_to_absent"
                    type="number"
                    min="1"
                    max="5"
                    className="w-20"
                    value={settings.half_day_to_absent_count}
                    onChange={(e) => setSettings({...settings, half_day_to_absent_count: parseInt(e.target.value) || 2})}
                  />
                  <span className="text-sm text-gray-600">Half Day = 1 Absent</span>
                </div>
                <p className="text-xs text-gray-500">Example: 2 half days = 1 absent count</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deduct_late">Deduction per Late (₹)</Label>
                <Input
                  id="deduct_late"
                  type="number"
                  min="0"
                  value={settings.deduction_per_late}
                  onChange={(e) => setSettings({...settings, deduction_per_late: parseInt(e.target.value) || 0})}
                />
                <p className="text-xs text-gray-500">Amount deducted from salary per late (0 = no deduction)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deduct_absent">Deduction per Absent (₹)</Label>
                <Input
                  id="deduct_absent"
                  type="number"
                  min="0"
                  value={settings.deduction_per_absent}
                  onChange={(e) => setSettings({...settings, deduction_per_absent: parseInt(e.target.value) || 0})}
                />
                <p className="text-xs text-gray-500">Amount deducted from salary per absent (0 = no deduction)</p>
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notify_late"
                  checked={settings.notify_on_late}
                  onChange={(e) => setSettings({...settings, notify_on_late: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="notify_late" className="cursor-pointer">
                  Send notification to staff on late mark
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notify_absent"
                  checked={settings.notify_on_absent}
                  onChange={(e) => setSettings({...settings, notify_on_absent: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="notify_absent" className="cursor-pointer">
                  Send notification to staff on absent
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overtime Settings */}
        <Card className="border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-green-500" />
              Overtime (OT) Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ot_rate">OT Rate per Hour (₹)</Label>
                <Input
                  id="ot_rate"
                  type="number"
                  min="0"
                  value={settings.overtime_rate_per_hour}
                  onChange={(e) => setSettings({...settings, overtime_rate_per_hour: parseInt(e.target.value) || 0})}
                />
                <p className="text-xs text-gray-500">0 = No overtime calculation</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="min_ot">Minimum OT Hours</Label>
                <Input
                  id="min_ot"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={settings.min_overtime_hours}
                  onChange={(e) => setSettings({...settings, min_overtime_hours: parseFloat(e.target.value) || 1})}
                />
                <p className="text-xs text-gray-500">Minimum hours to qualify for OT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-red-500" />
              College/Office Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lat">Latitude</Label>
                <Input
                  id="lat"
                  type="text"
                  placeholder="22.572645"
                  value={settings.location?.latitude || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    location: {...settings.location, latitude: e.target.value}
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lng">Longitude</Label>
                <Input
                  id="lng"
                  type="text"
                  placeholder="88.363892"
                  value={settings.location?.longitude || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    location: {...settings.location, longitude: e.target.value}
                  })}
                />
              </div>
            </div>

            <Button variant="outline" onClick={getCurrentLocation} className="w-full">
              <MapPin className="h-4 w-4 mr-2" />
              Get Current Location
            </Button>

            <MapPinPicker
              latitude={settings.location?.latitude}
              longitude={settings.location?.longitude}
              radiusMeters={settings.attendance_radius}
              onChange={(lat, lng) => setSettings(prev => ({
                ...prev,
                location: { ...prev.location, latitude: lat, longitude: lng }
              }))}
            />

            {settings.location?.latitude && settings.location?.longitude && (
              <div className="flex items-center justify-between text-xs bg-blue-50 border border-blue-200 rounded-lg p-2" data-testid="location-verify-row">
                <span className="text-blue-700">Verify the saved point is exactly your campus:</span>
                <a
                  href={`https://maps.google.com/?q=${settings.location.latitude},${settings.location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 font-medium underline"
                  data-testid="verify-on-maps-link"
                >
                  View on Google Maps
                </a>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="address">Address (Optional)</Label>
              <Input
                id="address"
                type="text"
                placeholder="College/Office address"
                value={settings.location?.address || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  location: {...settings.location, address: e.target.value}
                })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="radius">Allowed Radius (meters)</Label>
              <Input
                id="radius"
                type="number"
                min="50"
                max="5000"
                value={settings.attendance_radius}
                onChange={(e) => setSettings({...settings, attendance_radius: parseInt(e.target.value) || 500})}
              />
              <p className="text-xs text-gray-500">Staff must be within this radius to mark attendance</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="require_location"
                checked={settings.require_location}
                onChange={(e) => setSettings({...settings, require_location: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="require_location" className="cursor-pointer">
                Require location for attendance
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Working Days - Linked to Central Settings */}
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-blue-500" />
              Working Days
              <span className="text-xs font-normal bg-blue-100 text-blue-700 px-2 py-1 rounded-full ml-2">
                Central Settings
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Link2 className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">
                    Working Days is now managed centrally
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    All modules (Attendance, Routine, Salary) use the same working days setting from Master Settings → Working Days
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Current Working Days:</p>
              <div className="flex flex-wrap gap-2">
                {centralWorkingDays.working_days.map(day => (
                  <span key={day} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    {day}
                  </span>
                ))}
              </div>
              {!centralWorkingDays.is_configured && (
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Using default settings. Configure in Master Settings for custom schedule.
                </p>
              )}
            </div>

            <Button
              variant="outline"
              className="w-full border-blue-300 text-blue-700 hover:bg-blue-100"
              onClick={() => navigate('/master-settings/working-days')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Go to Working Days Settings
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Summary Preview */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-blue-900 mb-2">Settings Summary</h4>
          <div className="grid md:grid-cols-4 gap-4 text-sm mb-4">
            <div>
              <span className="text-blue-600">Office Hours:</span>
              <p className="font-medium">{settings.office_start_time} - {settings.office_end_time}</p>
            </div>
            <div>
              <span className="text-blue-600">Grace Period:</span>
              <p className="font-medium">{settings.grace_period_minutes} minutes</p>
            </div>
            <div>
              <span className="text-blue-600">Late After:</span>
              <p className="font-medium">{settings.late_after_minutes} min from start</p>
            </div>
            <div>
              <span className="text-blue-600">Allowed Radius:</span>
              <p className="font-medium">{settings.attendance_radius} meters</p>
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-4 text-sm border-t pt-4">
            <div>
              <span className="text-red-600">Late → Absent:</span>
              <p className="font-medium">{settings.late_to_absent_count} Late = 1 Absent</p>
            </div>
            <div>
              <span className="text-red-600">Half Day → Absent:</span>
              <p className="font-medium">{settings.half_day_to_absent_count} Half Day = 1 Absent</p>
            </div>
            <div>
              <span className="text-red-600">Deduction/Late:</span>
              <p className="font-medium">₹{settings.deduction_per_late || 0}</p>
            </div>
            <div>
              <span className="text-green-600">OT Rate/Hour:</span>
              <p className="font-medium">₹{settings.overtime_rate_per_hour || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
